import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { GridSize, Typography } from '@mui/material';
import Grid from 'components/core/Grid';
import { useMemo } from 'react';

import {
  MinusButton,
  PlusButton,
  PlusMinusInput,
  PlusMinusValue,
  PlusMinusWrapper,
  StyledTypographyTitle,
} from './styles';

export interface AppPlusMinusProps {
  value: number;
  /** Passes value + 1 */
  handleIncrease: (val?: number) => void;
  /** Passes value - 1 */
  handleDecrease: (val?: number) => void;
  id: string;
  title?: string;
  description?: string;
  label?: string;
  minimumValue?: number;
  maximumValue?: number;
  disabled?: boolean;
  isZeroBlank?: boolean;
  sm?: GridSize;
}

/** Simple minus (-), value, plus (+) button group
 * Takes code inspiration from NewPlusMinusFormField, PlusMinusText
 *
 */
export const AppPlusMinus = ({
  value,
  handleDecrease,
  handleIncrease,
  id,
  title = undefined,
  description = undefined,
  label = undefined,
  minimumValue = 0,
  maximumValue = Infinity,
  disabled = false,
  isZeroBlank = false,
  sm = 12,
}: AppPlusMinusProps) => {
  const handleDecreaseClick = () => {
    handleDecrease(value - 1);
  };

  const handleIncreaseClick = () => {
    handleIncrease(value + 1);
  };

  const isMinusButtonDisabled = value <= minimumValue || disabled;
  const isPlusButtonDisabled = value >= maximumValue || disabled;

  // TODO: verify if this code is entirely needed it was copied from NewPlusMinusFormField for safety
  // There may be a scenario where this value does not exist yet.
  // In that case, field.value value will be undefined, which would result
  // in a blank value being displayed
  const valueDisplay = useMemo(() => {
    if (value === undefined) return minimumValue;
    if (isZeroBlank && value === 0) return '';
    return value;
  }, [isZeroBlank, minimumValue, value]);

  const style = useMemo(() => {
    if (disabled) return { color: 'grey' };
    return {};
  }, [disabled]);

  return (
    <PlusMinusWrapper>
      {title ? (
        <Typography variant="h6" color="primary">
          {title}
        </Typography>
      ) : null}
      {description && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {description}
        </Typography>
      )}
      {label ? (
        <StyledTypographyTitle variant="caption" color="textSecondary">
          {label}
        </StyledTypographyTitle>
      ) : null}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={sm}>
          <PlusMinusInput>
            <MinusButton disabled={isMinusButtonDisabled} onClick={handleDecreaseClick} id={`${id}-${label}-minus`}>
              <RemoveIcon />
            </MinusButton>
            <PlusMinusValue style={style}>{valueDisplay}</PlusMinusValue>
            <PlusButton disabled={isPlusButtonDisabled} onClick={handleIncreaseClick} id={`${id}-${label}-plus`}>
              <AddIcon />
            </PlusButton>
          </PlusMinusInput>
        </Grid>
      </Grid>
    </PlusMinusWrapper>
  );
};
